/**
 * Back-office — Devis (groupe Commercial). Lecture `devis.lire` ; traitement `devis.gerer` ★.
 * La proposition affiche la marge par ligne et alerte sous le seuil : une ligne sous la marge minimale déclenche une
 * demande ◆ d'exception (permission `marge.exception.approuver`, approbateur distinct) — le client ne voit rien avant approbation.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Pagination, SearchField, Table, Tabs, TextField, useToast } from '@myron/design-system';
import { AlertTriangle, ClipboardCheck, Send, ShieldCheck, X } from 'lucide-react';
import { get, post } from '../../../shared/api/client';
import { SiPermission } from '../../../shared/auth/session';
import { Confirmation, EntetePage, ErreurFormulaire, EtatRequete } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, euros, LIBELLE_CONDITIONNEMENT } from '../../../shared/ui/format';
import { BadgeDevis } from '../../pro/fidelite/Devis';
import { LIBELLE_ACTION_DEVIS } from '../../pro/fidelite/api';

type TypeCond = 'PALETTE' | 'CARTON' | 'PACK';
interface Marge { coutRevientCentimes: number | null; margeCentimes: number | null; tauxBp: number | null; seuilBp: number; sousMinimum: boolean; coutInconnu: boolean }
interface DevisResume {
  id: string; reference: string; statut: string;
  entreprise: { id: string; reference: string; raisonSociale: string; segment: string | null } | null;
  demandeur: { id: string; nom: string; email: string } | null;
  nbLignes: number; totalHtCentimes: number | null; valideJusqua: string | null; creeLe: string; traitePar: string | null; exceptionEnAttente: boolean; version: number;
}
interface DevisDetail extends DevisResume {
  message: string | null; motifRefus: string | null; accordId: string | null; validiteJoursDefaut: number;
  lignes: { id: string; produitId: string; libelle: string; sku: string; conditionnementType: TypeCond; unitesBase: number | null; quantite: number; prixReferenceHtCentimes: number | null; prixProposeHtCentimes: number | null; marge: Marge | null; exceptionMarge: { statut: 'A_APPROUVER' | 'APPROUVEE' | 'REFUSEE'; tauxBp: number | null } | null }[];
  validation: { id: string; statut: string; demandeLe: string; expireLe: string; demandePar: string | null; traitePar: string | null; motifRejet: string | null } | null;
  historique: { date: string; auteur: string; action: string; statut: string }[];
}
type Liste = { donnees: DevisResume[]; page: number; taille: number; total: number; compteurs: Record<string, number> };

const taux = (bp: number | null | undefined) => (bp === null || bp === undefined ? '—' : `${(bp / 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
/** « 12,34 » → centimes ; invalide → null. */
const versCentimes = (s: string) => { const v = Number(s.replace(/\s/g, '').replace(',', '.')); return Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null; };
const versSaisie = (c: number | null | undefined) => (c ? (c / 100).toFixed(2).replace('.', ',') : '');

const ONGLETS = [
  { id: 'A_TRAITER', libelle: 'À traiter' },
  { id: 'EN_VALIDATION', libelle: 'En validation ◆' },
  { id: 'PROPOSE', libelle: 'Proposés' },
  { id: '', libelle: 'Tous' },
] as const;

export function FileDevis() {
  const [onglet, setOnglet] = useState<(typeof ONGLETS)[number]['id']>('A_TRAITER');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const nav = useNavigate();
  const requete = useQuery({ queryKey: ['admin', 'devis', onglet, q, page], queryFn: () => get<Liste>('/admin/devis', { statut: onglet, q, page, taille: 25 }), placeholderData: (x) => x });
  const c = requete.data?.compteurs;
  return (
    <div className="my-pile my-pile--large">
      <EntetePage titre="Devis" sousTitre="Demandes de prix pour volumes exceptionnels : étude, proposition avec contrôle de marge, suivi." />
      {c && (
        <div className="my-ligne" aria-label="Compteurs">
          <Badge ton="sable">{c.DEMANDE ?? 0} nouvelle(s)</Badge>
          <Badge ton="info">{c.EN_ETUDE ?? 0} en étude</Badge>
          <Badge ton="alerte">{c.EN_VALIDATION ?? 0} en validation ◆</Badge>
          <Badge ton="marque">{c.PROPOSE ?? 0} proposé(s)</Badge>
        </div>
      )}
      <Tabs label="Filtrer les devis" onglets={ONGLETS.map((o) => ({ id: o.id, libelle: o.libelle }))} actif={onglet} onChange={(id) => { setOnglet(id); setPage(1); }} />
      <SearchField label="Rechercher (référence ou client)" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      <EtatRequete
        requete={requete}
        vide={(d) => d.total === 0}
        titreVide="Aucun devis"
        enfants={(d) => (
          <>
            <Table<DevisResume>
              legende="File des devis"
              cle={(x) => x.id}
              lignes={d.donnees}
              onLigne={(x) => nav(`/admin/devis/${x.id}`)}
              colonnes={[
                { cle: 'ref', titre: 'Référence', rendu: (x) => <Link to={`/admin/devis/${x.id}`} className="my-code">{x.reference}</Link> },
                { cle: 'client', titre: 'Client', rendu: (x) => <span>{x.entreprise?.raisonSociale ?? '—'} {x.entreprise?.segment === 'GROSSISTE' && <Badge ton="accent">Grossiste</Badge>}</span> },
                { cle: 'date', titre: 'Reçu le', rendu: (x) => dateCourte(x.creeLe) },
                { cle: 'lignes', titre: 'Lignes', num: true, rendu: (x) => x.nbLignes },
                { cle: 'statut', titre: 'Statut', rendu: (x) => <span className="my-ligne"><BadgeDevis statut={x.statut} />{x.exceptionEnAttente && <Badge ton="alerte">◆ exception</Badge>}</span> },
                { cle: 'resp', titre: 'Suivi par', rendu: (x) => x.traitePar ?? '—' },
                { cle: 'total', titre: 'Total HT', num: true, rendu: (x) => euros(x.totalHtCentimes) },
              ]}
            />
            <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
          </>
        )}
      />
    </div>
  );
}

function CelluleMarge({ m }: { m: Marge | null | undefined }) {
  if (!m) return <span className="my-secondaire">—</span>;
  if (m.coutInconnu) return <Badge ton="alerte">Coût inconnu</Badge>;
  return (
    <span className="my-pile my-pile--serre">
      <span className="my-num" style={m.sousMinimum ? { color: 'var(--erreur)', fontWeight: 600 } : undefined}>{euros(m.margeCentimes)} · {taux(m.tauxBp)}</span>
      {m.sousMinimum && <span className="my-petit" style={{ color: 'var(--erreur)' }}><AlertTriangle className="my-icon" aria-hidden /> Sous le seuil ({taux(m.seuilBp)})</span>}
    </span>
  );
}

export function DetailDevisAdmin() {
  const { devisId = '' } = useParams();
  const cle = ['admin', 'devis', 'detail', devisId];
  const requete = useQuery({ queryKey: cle, queryFn: () => get<DevisDetail>(`/admin/devis/${devisId}`) });
  return (
    <div className="my-pile my-pile--large">
      <EtatRequete requete={requete} enfants={(d) => <ContenuDevis d={d} cle={cle} />} />
    </div>
  );
}

function ContenuDevis({ d, cle }: { d: DevisDetail; cle: string[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const [validite, setValidite] = useState(String(d.validiteJoursDefaut));
  const [decliner, setDecliner] = useState(false);
  const modifiable = d.statut === 'DEMANDE' || d.statut === 'EN_ETUDE';

  useEffect(() => {
    setSaisie(Object.fromEntries(d.lignes.map((l) => [l.id, versSaisie(l.prixProposeHtCentimes ?? l.prixReferenceHtCentimes)])));
  }, [d.id, d.version]);

  const prix = useMemo(() => d.lignes.map((l) => ({ id: l.id, prixProposeHtCentimes: versCentimes(saisie[l.id] ?? '') })), [d.lignes, saisie]);
  const complet = prix.every((p) => p.prixProposeHtCentimes !== null);
  const simulation = useQuery({
    queryKey: ['admin', 'devis', 'simulation', d.id, prix],
    queryFn: () => post<{ lignes: { id: string; marge: Marge | null }[] }>(`/admin/devis/${d.id}/simuler`, { lignes: prix.map((p) => ({ id: p.id, prixProposeHtCentimes: p.prixProposeHtCentimes ?? 0 })) }),
    enabled: modifiable,
    placeholderData: (x) => x,
  });
  const margeSimulee = new Map(simulation.data?.lignes.map((l) => [l.id, l.marge]));
  const sousSeuil = d.lignes.filter((l) => margeSimulee.get(l.id)?.sousMinimum).length;
  const total = complet ? d.lignes.reduce((s, l, i) => s + prix[i]!.prixProposeHtCentimes! * l.quantite, 0) : null;

  const maj = (n: DevisDetail) => { qc.setQueryData(cle, n); void qc.invalidateQueries({ queryKey: ['admin', 'devis'] }); };
  const etudier = useMutation({ mutationFn: () => post<DevisDetail>(`/admin/devis/${d.id}/etudier`), onSuccess: maj });
  const proposer = useMutation({
    mutationFn: () => post<DevisDetail>(`/admin/devis/${d.id}/proposer`, { lignes: prix, validiteJours: Number(validite) }, { version: d.version }),
    onSuccess: (n) => {
      maj(n);
      toast(n.statut === 'EN_VALIDATION'
        ? { ton: 'alerte', titre: 'Exception de marge demandée ◆', texte: 'Le devis sera proposé au client après approbation par une personne habilitée.' }
        : { ton: 'succes', titre: 'Proposition envoyée', texte: `Valable jusqu’au ${dateCourte(n.valideJusqua)}.` });
    },
  });
  const decline = useMutation({ mutationFn: (motif: string) => post<DevisDetail>(`/admin/devis/${d.id}/refuser`, { motif }), onSuccess: (n) => { maj(n); setDecliner(false); } });

  return (
    <>
      <EntetePage
        titre={`Devis ${d.reference}`}
        fil={[{ libelle: 'Devis', vers: '/admin/devis' }, { libelle: d.reference }]}
        sousTitre={<span className="my-ligne"><BadgeDevis statut={d.statut} /> {d.entreprise?.raisonSociale} ({d.entreprise?.reference}) · demandé par {d.demandeur?.nom} le {dateCourte(d.creeLe)}</span>}
        actions={
          <SiPermission permission="devis.gerer">
            {d.statut === 'DEMANDE' && <Button icone={ClipboardCheck} chargement={etudier.isPending} onClick={() => etudier.mutate()}>Prendre en charge</Button>}
            {modifiable && <Button variante="tertiaire" icone={X} onClick={() => setDecliner(true)}>Décliner</Button>}
          </SiPermission>
        }
      />
      <ErreurFormulaire erreur={etudier.error} />
      {d.message && <Card><p className="my-petit my-secondaire">Message du client</p><p>{d.message}</p></Card>}
      {d.statut === 'EN_VALIDATION' && d.validation && (
        <Alert ton="alerte" titre="Exception de marge en attente ◆" action={<Link to="/admin/validations" className="my-btn my-btn--secondaire my-btn--compact"><ShieldCheck className="my-icon" aria-hidden />Validations</Link>}>
          Demandée par {d.validation.demandePar} le {dateHeure(d.validation.demandeLe)} — expire le {dateHeure(d.validation.expireLe)}. Approbation par un utilisateur habilité (marge.exception.approuver), distinct du demandeur. Le client voit « en étude » sans prix.
        </Alert>
      )}
      {d.validation?.statut === 'REJETEE' && d.statut === 'EN_ETUDE' && <Alert ton="erreur" titre="Exception de marge rejetée">{d.validation.motifRejet ?? ''} — proposez de nouveaux prix.</Alert>}
      {d.statut === 'REFUSE' && d.motifRefus && <Alert ton="alerte" titre="Refusé">{d.motifRefus}</Alert>}
      {d.statut === 'ACCEPTE' && d.accordId && <Alert ton="succes" titre="Accepté par le client">Accord commercial créé à partir du devis.{' '}<Link to={`/admin/accords/${d.accordId}`}>Voir l’accord</Link></Alert>}

      <Table
        legende={`Lignes du devis ${d.reference}`}
        cle={(l) => l.id}
        lignes={d.lignes}
        colonnes={[
          { cle: 'p', titre: 'Produit', rendu: (l) => <span>{l.libelle} <span className="my-petit my-secondaire">{l.sku}</span></span> },
          { cle: 'c', titre: 'Cond.', rendu: (l) => `${LIBELLE_CONDITIONNEMENT[l.conditionnementType]}${l.unitesBase ? ` (${l.unitesBase} u.)` : ''}` },
          { cle: 'q', titre: 'Quantité', num: true, rendu: (l) => l.quantite },
          { cle: 'ref', titre: 'Prix catalogue HT', num: true, rendu: (l) => euros(l.prixReferenceHtCentimes) },
          {
            cle: 'prix', titre: 'Prix proposé HT', num: true,
            rendu: (l) => (modifiable
              ? <TextField label={`Prix proposé HT — ${l.libelle}`} inputMode="decimal" value={saisie[l.id] ?? ''} onChange={(e) => setSaisie((s) => ({ ...s, [l.id]: e.target.value }))} erreur={saisie[l.id] && versCentimes(saisie[l.id]!) === null ? 'Montant invalide' : undefined} />
              : euros(l.prixProposeHtCentimes)),
          },
          { cle: 'marge', titre: 'Marge', num: true, rendu: (l) => <CelluleMarge m={modifiable ? margeSimulee.get(l.id) : l.marge} /> },
          { cle: 'exc', titre: 'Exception ◆', rendu: (l) => (l.exceptionMarge ? <Badge ton={l.exceptionMarge.statut === 'APPROUVEE' ? 'succes' : l.exceptionMarge.statut === 'REFUSEE' ? 'erreur' : 'alerte'}>{l.exceptionMarge.statut === 'A_APPROUVER' ? 'À approuver' : l.exceptionMarge.statut === 'APPROUVEE' ? 'Approuvée' : 'Refusée'}</Badge> : '—') },
        ]}
      />

      {modifiable && (
        <SiPermission permission="devis.gerer">
          <Card as="section" aria-labelledby="t-proposition">
            <div className="my-pile">
              <h2 id="t-proposition" className="my-titre-4">Proposition</h2>
              {sousSeuil > 0 && <Alert ton="alerte" titre={`${sousSeuil} ligne(s) sous la marge minimale`}>La proposition sera soumise à une approbation ◆ avant d’être visible du client.</Alert>}
              <div className="my-form-ligne">
                <TextField label="Validité (jours)" type="number" min={1} max={90} value={validite} onChange={(e) => setValidite(e.target.value)} aide={`Par défaut : ${d.validiteJoursDefaut} jours (paramètre devis.validiteJours)`} />
                <p className="my-titre-4">Total HT : <span className="my-num">{euros(total)}</span></p>
              </div>
              <ErreurFormulaire erreur={proposer.error} />
              <div>
                <Button icone={sousSeuil ? ShieldCheck : Send} disabled={!complet || simulation.isFetching} chargement={proposer.isPending} onClick={() => proposer.mutate()}>
                  {sousSeuil ? 'Demander l’exception de marge ◆' : 'Envoyer la proposition au client'}
                </Button>
              </div>
            </div>
          </Card>
        </SiPermission>
      )}

      <Card as="section" aria-labelledby="t-hist-admin">
        <h2 id="t-hist-admin" className="my-titre-4">Historique</h2>
        <ol className="my-pile my-pile--serre">
          {d.historique.map((h, i) => <li key={i} className="my-petit"><span className="my-secondaire">{dateHeure(h.date)}</span> — {LIBELLE_ACTION_DEVIS[h.action] ?? h.action} · {h.auteur}</li>)}
        </ol>
      </Card>
      <Confirmation ouverte={decliner} titre="Décliner la demande ?" texte="Le motif est communiqué au client." libelleAction="Décliner" danger motif="obligatoire"
        chargement={decline.isPending} erreur={decline.error} onFermer={() => setDecliner(false)} onConfirmer={(m) => decline.mutate(m)} />
    </>
  );
}
